// Page-context bus — cross-bundle channel for live listing state.
//
// The office listing grids (InspectionsIndex, BuildingsIndex, …) live in the
// `application.js` webpack pack; the chat widget lives in the `chat-widget.ts`
// pack. They are SEPARATE bundles, so a module-level singleton is NOT shared
// between them. This bus therefore communicates exclusively through `window`:
//
//   - publishListContext(patch) stamps the latest value on a window key AND
//     dispatches a CustomEvent so already-subscribed listeners react live.
//   - subscribeListContext(cb) reads the current value synchronously (so a
//     widget that mounts AFTER the grid still sees the latest state) and then
//     listens for subsequent CustomEvents.
//
// Office is a multi-page app: every navigation is a full page load that resets
// `window`, so there is no stale cross-page leakage — the bus naturally clears
// when the user leaves a listing page.
const WINDOW_VALUE_KEY = '__ipaLiveListContext';
const EVENT_NAME = 'ipa:live-list-context';
function busWindow() {
    return typeof window === 'undefined' ? null : window;
}
/**
 * Publish the current listing state. Pass `null` to clear (e.g. when a grid
 * unmounts or the active filter set becomes unknowable).
 */
export function publishListContext(listContext) {
    const win = busWindow();
    if (!win)
        return;
    win[WINDOW_VALUE_KEY] = listContext;
    win.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: listContext }));
}
/** Read the latest published listing state without subscribing. */
export function getCurrentListContext() {
    var _a;
    const win = busWindow();
    if (!win)
        return null;
    return (_a = win[WINDOW_VALUE_KEY]) !== null && _a !== void 0 ? _a : null;
}
/**
 * Subscribe to listing-state changes. Invokes `callback` immediately with the
 * current value (or null) and again on every subsequent publish. Returns an
 * unsubscribe function.
 */
export function subscribeListContext(callback) {
    const win = busWindow();
    if (!win)
        return () => undefined;
    // Synchronous replay of the current value so a late-mounting widget is fresh.
    callback(getCurrentListContext());
    const handler = (event) => {
        var _a;
        callback((_a = event.detail) !== null && _a !== void 0 ? _a : null);
    };
    win.addEventListener(EVENT_NAME, handler);
    return () => {
        win.removeEventListener(EVENT_NAME, handler);
    };
}
// Exported for tests that need to reset window state between cases.
export const __testing = { WINDOW_VALUE_KEY, EVENT_NAME };
